import { SlotContent } from '../types/Slot';

/**
 * Renders slot content, supporting both static ReactNode and render functions with optional arguments.
 *
 * Enables deferred rendering of expensive content for performance optimization
 * in components like tooltips, popovers, and accordions. Also supports render functions
 * that receive arguments for dynamic content.
 *
 * @template T - Tuple type for render function arguments
 * @param content - The slot content to render. Can be:
 *   - A ReactNode (rendered immediately)
 *   - A function returning ReactNode (called with provided args)
 * @param args - Optional arguments to pass to the render function
 * @returns The rendered ReactNode
 * @example
 * // Static: renderSlotContent(<div>Content</div>)
 * // Lazy (no args): renderSlotContent(() => <ExpensiveComponent />)
 * // Dynamic (with args): renderSlotContent((isActive) => <div>{isActive}</div>, true)
 */
export function renderSlotContent<T extends any[]>(
  content: SlotContent<T>,
  ...args: T
) {
  if (typeof content === 'function') {
    return content(...args);
  }

  return content;
}
